import {
  createInstance,
  OptimizelyUserContext,
  UserAttributes,
  type Client,
} from '@optimizely/optimizely-sdk'
import {
  OptimizelyContextType,
  OptimizelyFeatureFlags,
  OptimizelyDecisionWithoutUserContext,
  defaultOptimizelyContext,
} from 'models/Optimizely'
import { UrlOverrideMap } from 'utils/optimizely/parseOptimizelyUrlOverrides'
import { logger } from 'utils/logger'

/**
 * Singleton class for Optimizely SDK
 * This class is responsible for initializing the Optimizely SDK and providing methods
 * for getting feature flags, bucketing users into experiments, and tracking events.
 */
export class OptimizelySDK {
  private static instance: OptimizelySDK
  private client: Client | null = null
  private isInitializing = false
  private initPromise: Promise<Client | null> | null = null
  private lastInitTime: number | null = null
  private readonly DATAFILE_REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes in milliseconds

  private constructor() {
    // Private constructor to prevent direct construction calls with 'new'
  }
  /**
   * Get the singleton instance of OptimizelySDK
   */
  public static getInstance(): OptimizelySDK {
    if (!OptimizelySDK.instance) {
      OptimizelySDK.instance = new OptimizelySDK()
    }

    return OptimizelySDK.instance
  }

  /**
   * Check if the client should be refreshed based on the last initialization time
   * @returns boolean indicating if client needs refresh
   */
  private shouldRefreshClient(): boolean {
    if (!this.lastInitTime) {
      return true
    }

    return Date.now() - this.lastInitTime > this.DATAFILE_REFRESH_INTERVAL
  }

  /**
   * Fetches datafile from Optimizely CDN
   * @param sdkKey - The Optimizely SDK key
   * @returns Promise that resolves to the datafile string or null if fetch fails
   */
  private async fetchDatafile(sdkKey: string): Promise<string | null> {
    try {
      const url = `https://cdn.optimizely.com/datafiles/${sdkKey}.json`

      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      if (!response.ok) {
        logger('error', new Error(`Failed to fetch Optimizely datafile: ${response.status}`), {
          context: { sdkKey, status: response.status, statusText: response.statusText },
        })

        return null
      }

      const datafile = await response.text()

      return datafile
    } catch (error) {
      logger('error', new Error('Error fetching Optimizely datafile from CDN'), {
        context: { error, sdkKey },
      })

      return null
    }
  }

  /**
   * Initialize the Optimizely SDK client
   * @returns Promise that resolves to the Optimizely client or null if initialization fails
   */
  public async initialize(): Promise<Client | null> {
    // If client exists and is not stale, return it
    if (this.client && !this.shouldRefreshClient()) {
      return this.client
    }

    // If initialization is in progress, return the existing promise
    if (this.isInitializing && this.initPromise) {
      return this.initPromise
    }

    // Start initialization
    this.isInitializing = true
    this.initPromise = this.initializeClient()

    try {
      const newClient = await this.initPromise

      if (newClient) {
        this.client = newClient
        this.lastInitTime = Date.now()
      }

      return newClient
    } catch (error) {
      logger('error', new Error('Optimizely SDK failed to initialize'), { context: { error } })
      return null
    } finally {
      this.isInitializing = false
      this.initPromise = null
    }
  }

  /**
   * Create a user context for the given user ID and attributes
   * @param userId User ID to create context for
   * @param userAttributes Optional user attributes
   * @returns UserContext or null if client is not initialized
   */
  public async createUserContext(
    userId: string,
    userAttributes?: UserAttributes,
  ): Promise<OptimizelyUserContext | null> {
    // TODO: cache user contexts
    const client = await this.initialize()

    if (!client) {
      return null
    }

    // Create a new user context directly from the client
    return client.createUserContext(userId, userAttributes)
  }

  /**
   * Helper method to process decisions for a specific type (feature flags or experiments)
   * @param userContext The Optimizely user context
   * @param keys The keys to get decisions for
   * @param defaultValues The default values to use if no decision is found
   * @returns The processed decisions
   */
  private processDecisions<T extends string>(
    userContext: OptimizelyUserContext | null,
    keys: T[],
    defaultValues: Record<T, OptimizelyDecisionWithoutUserContext>,
  ): Record<T, OptimizelyDecisionWithoutUserContext> {
    const result = { ...defaultValues }

    if (!userContext) {
      return result
    }

    const decisions = userContext.decideForKeys(keys)

    for (const [key, decision] of Object.entries(decisions)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { userContext, ...decisionWithoutUserContext } = decision
      result[key as T] = decisionWithoutUserContext
    }

    return result
  }

  /**
   * Get all decisions (feature flags and experiments) for a user
   * @param userId User ID to get decisions for
   * @param userAttributes Optional user attributes
   * @param urlOverrides Optional URL parameter overrides (from server-side parsing)
   * @returns Promise that resolves to an OptimizelyContextType object
   */
  public async getDecisions(
    userId: string,
    userAttributes?: UserAttributes,
    urlOverrides?: UrlOverrideMap,
  ): Promise<OptimizelyContextType> {
    try {
      const userContext = await this.createUserContext(userId, userAttributes)

      // Get normal Optimizely decisions
      const decisions = this.processDecisions(userContext, Object.values(OptimizelyFeatureFlags), {
        ...defaultOptimizelyContext.featureFlags,
      })

      // Apply URL overrides if provided
      if (urlOverrides) {
        for (const [flagName, override] of Object.entries(urlOverrides)) {
          if (flagName in decisions) {
            decisions[flagName as OptimizelyFeatureFlags] = override
          }
        }
      }

      return {
        featureFlags: decisions,
      }
    } catch (e) {
      logger('error', new Error('Error getting Optimizely decisions'), { context: { e } })
      return defaultOptimizelyContext
    }
  }

  private async initializeClient(): Promise<Client | null> {
    try {
      const sdkKey = process.env.OPTIMIZELY_NPO_FRONTEND_SDK_KEY

      if (!sdkKey) {
        logger('error', new Error('Optimizely SDK key is not defined'))
        return null
      }

      // Fetch the datafile directly
      const datafile = await this.fetchDatafile(sdkKey)

      if (datafile === null) {
        logger('warn', new Error('Failed to fetch Optimizely datafile.'))

        return null
      }

      // Initialize with pre-fetched datafile to prevent <script> fetches
      const client = createInstance({ datafile })

      if (!client) {
        logger('error', new Error('Failed to create Optimizely client instance'))
        return null
      }

      return client
    } catch (error) {
      logger('error', new Error('Optimizely SDK failed to initialize'), { context: { error } })
      return null
    }
  }
}

// Export a default instance for convenience
const instance = OptimizelySDK.getInstance()
export default instance
